import type { Post } from "@/lib/news/types";
import hero from "@/assets/news/samla-lan-150000-medsokande.jpg";

/** Långivare och förmedlare som klarar samlingslån på 150 000 kr utan säkerhet. */
const SAMLA_ALLA = [
  "northmillsamla", "lendo", "sambla", "enklare", "advisa", "axo",
  "loansse", "finlo", "ekonomen", "thorn", "banky", "plus1stora",
];

/** Näst bästa alternativen när huvudlistan inte räcker. */
const NAST_BAST = ["northmill", "brixopl", "moneezy", "enkelfinans", "klaralan"];

export const SAMLA_LAN_150000_MEDSOKANDE: Record<string, Post> = {
  "samla-lan-150000-kr-utan-sakerhet-medsokande": {
    title: "Samla lån 150 000 kr utan säkerhet med medsökande – jämförelse 2026",
    intro:
      "Har du flera dyra krediter och känner att månadskostnaden äter upp lönen? Du är inte ensam. Att samla lån på 150 000 kr utan säkerhet – gärna med medsökande – kan sänka räntan rejält och ge dig en enda avi i stället för sju. Här jämför vi alla banker och förmedlare som klarar 150 000 kr, visar vad det kostar i kronor och går igenom exakt hur en medsökande påverkar din ränta.",
    author: "Erik Lindqvist",
    authorTitle: "Ekonomiredaktör",
    published: "2026-09-03",
    updated: "2026-09-03",
    heroSrc: hero,
    heroAlt: "Par går igenom sina lån vid köksbordet för att samla lån 150 000 kr med medsökande",
    topLenderId: "northmillsamla",
    bottomLenderId: "lendo",
    topLenderTitle: "Bäst i test – samla lån 150 000 kr utan säkerhet",
    bottomLenderTitle: "Vår rekommendation för flest bud med medsökande",
    reviewsLenderId: "lendo",
    cta: { to: "/samla-lan", label: "Jämför samlingslån på 150 000 kr" },
    calculator: {
      title: "Räkna på samlingslån 150 000 kr",
      intro:
        "Dra i reglagen och se vad ett samlingslån på 150 000 kr kostar per månad, hur mycket räntan blir totalt och vilken effektiv ränta du faktiskt betalar.",
      note:
        "Månadskostnaden styrs av lånebelopp, löptid och nominell ränta, men det är den effektiva räntan som visar den verkliga kostnaden eftersom uppläggningsavgift och aviavgift räknas in. En kortare löptid ger högre månadskostnad men klart lägre total lånekostnad – och med medsökande hamnar räntan ofta flera procentenheter lägre.",
      amount: 150000,
      minAmount: 10000,
      maxAmount: 600000,
      months: 96,
      maxMonths: 180,
      rate: 8.95,
      minRate: 3,
      maxRate: 25,
      setupFee: 495,
      invoiceFee: 29,
    },
    faq: [
      {
        q: "Kan man samla lån på 150 000 kr utan säkerhet?",
        a: "Ja. Ett samlingslån är ett blancolån, alltså ett lån utan säkerhet. Banken tittar på din inkomst, dina nuvarande krediter och din betalningshistorik i stället för på en bostad eller en bil.",
      },
      {
        q: "Hur mycket sänker en medsökande räntan?",
        a: "Skillnaden är ofta 1–4 procentenheter eftersom två inkomster minskar bankens risk. På 150 000 kr över åtta år kan det motsvara 15 000–30 000 kr lägre totalkostnad.",
      },
      {
        q: "Vem kan vara medsökande?",
        a: "Vanligen partner, förälder, syskon eller nära vän med egen inkomst och utan aktiva betalningsanmärkningar. Ni behöver inte bo på samma adress hos alla banker, men flera kräver det.",
      },
      {
        q: "Vad händer med mina gamla krediter?",
        a: "Banken löser normalt dem direkt genom att betala fordringsägarna. Kontrollera ändå själv att varje kredit är avslutad – ett kvarvarande kort med kvar kredit är den vanligaste anledningen till att skulden växer igen.",
      },
      {
        q: "Vad kostar ett samlingslån på 150 000 kr per månad?",
        a: "Med 8,95 procent ränta på åtta år blir månadskostnaden cirka 2 200 kr inklusive aviavgift. Total kostnad landar då runt 211 000 kr, vilket motsvarar en effektiv ränta kring 9,5 procent.",
      },
      {
        q: "Påverkas min medsökande om jag inte betalar?",
        a: "Ja. Ni är solidariskt betalningsansvariga, vilket innebär att banken kan kräva hela beloppet av vem som helst av er. Uteblivna betalningar syns i båda parternas kreditupplysning.",
      },
      {
        q: "Kan jag samla lån med betalningsanmärkning?",
        a: "Det är svårt men inte omöjligt. Med en medsökande utan anmärkning ökar chansen märkbart. Har du en pågående skuld hos Kronofogden avslås ansökan i regel.",
      },
      {
        q: "Hur många kreditupplysningar tas via en förmedlare?",
        a: "En enda. Förmedlaren hämtar upplysningen och skickar din ansökan vidare till upp till 40 banker, vilket är betydligt skonsammare för din kreditvärdighet än att söka separat.",
      },
    ],
    blocks: [
      { t: "h2", text: "Samla lån 150 000 kr utan säkerhet – kort svar" },
      { t: "p", text: "Ja, det går att samla lån på 150 000 kr utan säkerhet. Du ansöker om ett blancolån där banken löser dina befintliga krediter och ersätter dem med ett lån, en ränta och en avi. Med en medsökande blir räntan i regel lägre eftersom två inkomster minskar bankens risk." },
      { t: "p", text: "Vi vet att den här sidan ofta läses av någon som har tappat överblicken. Därför visar vi kostnaderna öppet, i kronor, utan pekpinnar – så att du kan fatta ett beslut du står för även nästa månad." },
      {
        t: "stats",
        items: [
          { label: "Typisk ränta 2026", value: "5,95–14,95 %" },
          { label: "Vanlig löptid", value: "5–12 år" },
          { label: "Möjlig besparing", value: "2 000–4 000 kr/mån" },
        ],
      },

      { t: "h2", text: "Så fungerar ett samlingslån" },
      { t: "p", text: "Ett samlingslån är ett vanligt privatlån där hela eller delar av beloppet används för att lösa dina befintliga krediter. Banken betalar dina kortkrediter, snabblån och avbetalningar direkt till fordringsägarna, och du får ett nytt lån med en tydlig amorteringsplan." },
      { t: "h3", text: "Varför räntan sjunker" },
      { t: "p", text: "Kontokrediter och kortköp ligger ofta på 18–25 procent, medan ett blancolån på 150 000 kr sällan överstiger 15 procent och ofta hamnar under 10. Skillnaden beror på att ett samlingslån har en fast amorteringsplan och därmed lägre risk för banken." },
      { t: "h3", text: "Varför en avi är mer värd än den låter" },
      { t: "p", text: "Sju avier innebär sju förfallodagar och sju chanser att missa en betalning. Varje missad avi kostar påminnelseavgift och dröjsmålsränta – och i värsta fall en anmärkning. En avi tar bort hela den risken." },

      { t: "h2", text: "Bäst i test – samla lån 150 000 kr 2026" },
      { t: "p", text: "Vår ranking väger ränta och avgifter tyngst, följt av maxbelopp, hur väl banken hanterar direktlösen av gamla krediter, möjligheten till medsökande och kundomdömen. Ingen långivare kan köpa sig till en placering." },
      { t: "lenders", ids: SAMLA_ALLA, title: "Alla banker och förmedlare som klarar 150 000 kr", note: "Räntan sätts individuellt efter kreditprövning. Räkneexemplen är typexempel enligt konsumentkreditlagen." },

      { t: "h2", text: "Näst bästa alternativen" },
      { t: "p", text: "Får du avslag hos storbankerna kan en nischbank fungera. Räntan blir högre, men fortfarande betydligt lägre än på en kontokredit." },
      { t: "lenders", ids: NAST_BAST, title: "Alternativ när huvudlistan inte räcker" },

      { t: "h2", text: "Medsökande – så mycket påverkar det" },
      { t: "p", text: "Med en medsökande bedömer banken er samlade inkomst och betalningsförmåga. Det ger tre effekter: högre chans att bli beviljad, högre maxbelopp och lägre ränta." },
      {
        t: "table",
        caption: "Exempel: 150 000 kr på 8 år, annuitet, 495 kr uppläggning, 29 kr avi",
        head: ["Situation", "Ränta", "Månadskostnad", "Total kostnad"],
        rows: [
          ["Ensam sökande, tunn historik", "13,95 %", "2 620 kr", "251 500 kr"],
          ["Ensam sökande, stabil inkomst", "10,95 %", "2 390 kr", "229 400 kr"],
          ["Med medsökande", "8,95 %", "2 230 kr", "214 600 kr"],
          ["Med medsökande och hög inkomst", "6,95 %", "2 080 kr", "200 300 kr"],
        ],
      },
      { t: "p", text: "Skillnaden mellan översta och nedersta raden är drygt 51 000 kr. Testa dina egna siffror i vår [lånekalkyl](/guider/lanekalkyl) innan du signerar." },

      { t: "h2", text: "Solidariskt betalningsansvar – det viktiga finstilta" },
      { t: "p", text: "Ni ansvarar båda för hela skulden, inte hälften var. Banken får kräva vem som helst av er på hela beloppet. Prata igenom vad som händer vid separation, sjukdom eller arbetslöshet innan ni skriver under." },
      {
        t: "box",
        tone: "warning",
        title: "Innan ni signerar tillsammans",
        items: [
          "Bestäm skriftligt vem som betalar vad varje månad.",
          "Kontrollera att båda klarar hela månadskostnaden ensam om det behövs.",
          "Kom ihåg att lånet syns i båda parternas kreditutrymme vid framtida bolån.",
          "Ta reda på om banken tillåter att en medsökande skrivs av senare.",
        ],
      },

      { t: "h2", text: "Vad kostar 150 000 kr på olika löptider?" },
      {
        t: "table",
        caption: "Annuitetslån, 8,95 % nominell ränta, 29 kr aviavgift",
        head: ["Löptid", "Månadskostnad", "Räntekostnad", "Total kostnad", "Effektiv ränta"],
        rows: [
          ["5 år", "3 138 kr", "37 300 kr", "188 300 kr", "9,8 %"],
          ["8 år", "2 230 kr", "63 100 kr", "214 600 kr", "9,5 %"],
          ["10 år", "1 924 kr", "80 400 kr", "234 300 kr", "9,4 %"],
          ["12 år", "1 723 kr", "98 100 kr", "252 300 kr", "9,3 %"],
        ],
      },
      { t: "p", text: "Notera att månadskostnaden sjunker med 1 415 kr mellan fem och tolv år – men räntekostnaden ökar med drygt 60 000 kr. Välj kortast löptid din budget klarar." },

      { t: "h2", text: "Så mycket kan du spara på att samla" },
      {
        t: "table",
        caption: "Typisk skuldbild före och efter samling",
        head: ["Kredit", "Skuld", "Ränta", "Månadskostnad"],
        rows: [
          ["Kontokredit bank", "40 000 kr", "19,9 %", "1 200 kr"],
          ["Kreditkort", "35 000 kr", "22,4 %", "1 100 kr"],
          ["Snabblån", "25 000 kr", "24,9 %", "1 250 kr"],
          ["Avbetalning möbler", "30 000 kr", "17,9 %", "900 kr"],
          ["Delbetalning e-handel", "20 000 kr", "23,9 %", "850 kr"],
          ["Totalt före", "150 000 kr", "≈21 %", "5 300 kr"],
          ["Samlingslån 8 år", "150 000 kr", "8,95 %", "2 230 kr"],
        ],
      },
      { t: "p", text: "I exemplet frigörs drygt 3 000 kr i månaden. Använder du hela mellanskillnaden till extra amortering är lånet löst på under fem år." },

      { t: "h2", text: "Krav för att bli beviljad" },
      {
        t: "ul",
        items: [
          "Minst 18 år – många banker kräver 20 eller 23 år vid högre belopp.",
          "Folkbokförd i Sverige med svenskt personnummer och BankID.",
          "Fast eller regelbunden inkomst, ofta minst 150 000–200 000 kr per år.",
          "Inga aktiva betalningsanmärkningar hos flertalet banker.",
          "Ingen pågående skuld hos [Kronofogden](https://www.kronofogden.se).",
        ],
      },
      { t: "h3", text: "Om du har anmärkning" },
      { t: "p", text: "En medsökande utan anmärkning kan vända ett nej till ett ja. Läs mer på vår sida om [lån med betalningsanmärkning](/lan-med-betalningsanmarkning-bast-i-test-30000-kr)." },

      { t: "h2", text: "Låneförmedlare eller bank – vad är bäst?" },
      {
        t: "table",
        caption: "Jämförelse mellan ansökningsvägarna",
        head: ["", "Låneförmedlare", "Direkt hos bank"],
        rows: [
          ["Antal bud", "Upp till 40 banker", "Ett"],
          ["Kreditupplysningar", "En", "En per bank"],
          ["Handläggningstid", "Minuter till 1 dygn", "1–3 bankdagar"],
          ["Medsökande", "Stöds av samtliga", "Varierar"],
          ["Kostnad för dig", "Gratis", "Gratis"],
        ],
      },
      { t: "p", text: "För 150 000 kr är en förmedlare nästan alltid rätt startpunkt – du ser hela marknadens ränta på en enda upplysning." },

      { t: "h2", text: "Så ansöker du – steg för steg" },
      {
        t: "steps",
        title: "Från ansökan till löst kreditlista",
        items: [
          "Lista alla krediter med skuld, ränta och månadskostnad.",
          "Räkna igenom din nya månadskostnad i kalkylatorn nedan.",
          "Fyll i ansökan och lägg till medsökande direkt i formuläret.",
          "Signera med BankID – kreditupplysningen tas en gång.",
          "Jämför buden och välj lägst effektiv ränta, inte lägst månadskostnad.",
          "Godkänn direktlösen och kontrollera att varje gammal kredit avslutas.",
        ],
      },

      { t: "h2", text: "För- och nackdelar med att samla 150 000 kr" },
      {
        t: "proscons",
        pros: [
          "Räntan halveras ofta jämfört med kortkrediter",
          "En avi och ett slutdatum i stället för sju",
          "Medsökande sänker räntan ytterligare",
          "Ingen säkerhet i bostad eller bil krävs",
        ],
        cons: [
          "Lång löptid kan höja den totala räntekostnaden",
          "Uppläggningsavgift tillkommer",
          "Solidariskt ansvar binder er båda",
          "Frestande att börja använda de gamla krediterna igen",
        ],
      },

      { t: "h2", text: "Räntetak, kostnadstak och ångerrätt" },
      { t: "p", text: "Den nominella räntan får aldrig överstiga Riksbankens referensränta plus 20 procentenheter. Du har alltid 14 dagars ångerrätt enligt konsumentkreditlagen och rätt att lösa lånet i förtid utan extra avgift." },
      {
        t: "box",
        tone: "info",
        title: "Det här gäller alltid",
        items: [
          "Kreditprövning är obligatorisk – ingen seriös bank hoppar över den.",
          "Långivaren måste ha tillstånd från Finansinspektionen.",
          "Ränteändringar ska meddelas i förväg och motiveras.",
          "Du har rätt till en amorteringsplan utan kostnad.",
        ],
      },

      { t: "h2", text: "Vanliga misstag när man samlar lån" },
      {
        t: "box",
        tone: "warning",
        title: "Fem fällor att undvika",
        items: [
          "Att inte avsluta de gamla kontokrediterna efter lösen.",
          "Att välja längsta löptiden bara för att månadskostnaden ska se snäll ut.",
          "Att jämföra nominell ränta i stället för effektiv ränta.",
          "Att söka separat hos åtta banker och samla på sig kreditupplysningar.",
          "Att låna extra ovanpå samlingen för att köpa något nytt.",
        ],
      },

      { t: "h2", text: "Vad gör du efter att lånet är utbetalt?" },
      {
        t: "ul",
        items: [
          "Begär skriftlig bekräftelse på att varje kredit är avslutad.",
          "Säg upp kortkrediter du inte behöver – inte bara nollställ dem.",
          "Sätt en fast överföring till buffertsparande samma dag som lönen kommer.",
          "Använd hela eller delar av besparingen till extra amortering.",
        ],
      },

      { t: "h2", text: "Alternativ till samlingslån" },
      {
        t: "ul",
        items: [
          "Utökat bolån om du äger bostad – ofta lägst ränta av alla alternativ.",
          "Omförhandling av räntan hos befintlig långivare.",
          "Avbetalningsplan direkt hos fordringsägaren, ibland räntefri.",
          "Kommunens budget- och skuldrådgivning, som alltid är kostnadsfri.",
          "Jämför också [privatlån med lägst ränta](/privatlan-jamforelse-lagst-ranta-2026) och beloppssidan [samla lån 150 000 kr](/samla-lan/150000-kr).",
        ],
      },

      { t: "h2", text: "Så granskar vi långivarna" },
      { t: "p", text: "Vi väger ränta, avgifter, maxbelopp, hantering av direktlösen, transparens i villkoren, tillstånd hos Finansinspektionen och kundomdömen. Läs mer om [hur vi granskar](/om-oss/hur-vi-granskar) och om vår [redaktion](/om-oss/redaktion)." },

      { t: "h2", text: "Sammanfattning" },
      { t: "p", text: "Att samla lån på 150 000 kr utan säkerhet är ofta det enskilt mest lönsamma du kan göra med en spretig kreditlista. Med en medsökande sjunker räntan ytterligare. Välj kortast möjliga löptid, jämför effektiv ränta – och avsluta de gamla krediterna samma vecka." },

      { t: "h2", text: "Så bedömer banken din ansökan på 150 000 kr" },
      { t: "p", text: "Vid ett blancolån på 150 000 kr görs en kvar-att-leva-på-kalkyl. Banken tar din inkomst efter skatt, drar av boendekostnad, schablon för levnadsomkostnader, befintliga krediter och en påhittad ränteuppgång på några procentenheter. Det som återstår ska räcka till den nya månadskostnaden." },
      { t: "p", text: "Det är också därför en samling ofta går igenom trots att en ny kredit hade nekats: dina gamla krediter löses samma dag och försvinner ur kalkylen. Din skuld blir inte större – den blir billigare och mer överblickbar." },
      { t: "h3", text: "Vad som väger tyngst" },
      {
        t: "table",
        caption: "Faktorer i kreditprövningen vid 150 000 kr",
        head: ["Faktor", "Påverkan på räntan", "Vad du kan göra"],
        rows: [
          ["Inkomst efter skatt", "Mycket stor", "Redovisa all inkomst, även extrajobb"],
          ["Medsökande", "Stor", "Lägg till en person med egen inkomst"],
          ["Antal kreditförfrågningar", "Medel", "Använd förmedlare – en upplysning"],
          ["Anställningsform", "Medel", "Fast anställning väger tyngst"],
          ["Betalningshistorik", "Stor", "Betala alla avier i tid i minst sex månader"],
          ["Boendeform", "Liten till medel", "Ägt boende ger något lägre ränta"],
        ],
      },

      { t: "h2", text: "Direktlösen – det avgörande momentet" },
      { t: "p", text: "Direktlösen innebär att banken betalar dina gamla krediter direkt till fordringsägarna i stället för att sätta in pengarna på ditt konto. Det är både snabbare och tryggare, eftersom pengarna aldrig hinner användas till något annat." },
      { t: "p", text: "Du behöver lämna kontoinformation, aktuellt saldo och OCR-nummer för varje kredit. Har du sju krediter tar det en kvart att sammanställa – och det är den kvarten som avgör om samlingen faktiskt blir av." },
      { t: "h3", text: "Kontrollera att krediterna avslutas" },
      { t: "p", text: "En löst kontokredit är inte automatiskt en avslutad kontokredit. Ring eller mejla varje långivare och begär skriftlig bekräftelse på att avtalet är uppsagt. Annars ligger kreditutrymmet kvar och frestelsen med det." },

      { t: "h2", text: "Räkneexempel: familjen som samlade sju krediter" },
      { t: "p", text: "Ett typiskt hushåll vi räknat på hade 150 000 kr fördelat på sju krediter med en snittränta runt 21 procent och 5 300 kr i samlad månadskostnad. Efter samling till 8,95 procent på åtta år blev månadskostnaden 2 230 kr." },
      {
        t: "table",
        caption: "Före och efter samling",
        head: ["", "Före", "Efter"],
        rows: [
          ["Antal krediter", "7", "1"],
          ["Snittränta", "≈21 %", "8,95 %"],
          ["Månadskostnad", "5 300 kr", "2 230 kr"],
          ["Antal förfallodagar", "7", "1"],
          ["Slutdatum", "Okänt", "Om 8 år"],
        ],
      },
      { t: "p", text: "Väljer hushållet dessutom att lägga hela mellanskillnaden på extra amortering är lånet slutbetalt efter drygt fyra år i stället för åtta, och den totala räntekostnaden mer än halveras." },

      { t: "h2", text: "Fast eller rörlig ränta på samlingslånet?" },
      { t: "p", text: "De flesta blancolån har rörlig ränta som följer marknadsläget och din egen riskprofil. Vissa banker erbjuder bunden ränta mot ett litet påslag. Bundet ger förutsägbarhet, rörligt ger flexibilitet och oftast lägre startränta." },
      { t: "p", text: "Har du marginal i budgeten är rörlig ränta normalt billigast över tid. Är budgeten precis kan tryggheten i en bunden ränta vara värd påslaget – särskilt om du redan har haft en period där ekonomin svajade." },

      { t: "h2", text: "Amorteringsstrategi efter samlingen" },
      {
        t: "ul",
        items: [
          "Behåll den gamla månadskostnaden och lägg mellanskillnaden på amortering.",
          "Sätt en fast överföring samma dag som lönen kommer – automatik slår viljestyrka.",
          "Extraamortera vid skatteåterbäring, bonus eller semesterersättning.",
          "Bygg först en buffert på en månadslön, sedan full fart på amorteringen.",
          "Se över räntan efter tolv prickfria månader – många banker omförhandlar.",
        ],
      },

      { t: "h2", text: "Vad händer om ekonomin ändå vänder nedåt?" },
      { t: "p", text: "Hör av dig till banken innan förfallodagen, inte efter. De flesta erbjuder amorteringsfrihet i tre till sex månader eller en förlängd löptid. Det kostar ränta, men det är oändligt mycket billigare än inkasso och anmärkningar." },
      {
        t: "box",
        tone: "tip",
        title: "Om det blir tungt",
        items: [
          "Begär amorteringsfrihet skriftligt och i god tid.",
          "Kontakta kommunens budget- och skuldrådgivning – kostnadsfritt.",
          "Prioritera hyra, el och mat före kreditbetalningar.",
          "Undvik nya krediter för att täcka en avi – det förvärrar alltid läget.",
        ],
      },

      { t: "h2", text: "Vanliga frågor om medsökande" },
      { t: "h3", text: "Måste vi bo tillsammans?" },
      { t: "p", text: "Inte hos alla banker. Flera accepterar medsökande som bor på annan adress, medan andra kräver samma hushåll. Det framgår alltid i ansökan innan ni signerar." },
      { t: "h3", text: "Kan vi ta bort en medsökande senare?" },
      { t: "p", text: "Ibland. Det kräver en ny kreditprövning där den kvarvarande låntagaren ensam ska klara hela lånet. Fråga om möjligheten redan innan ni tecknar avtalet." },
      { t: "h3", text: "Påverkar lånet vårt framtida bolån?" },
      { t: "p", text: "Ja. Ett blancolån på 150 000 kr räknas in i bolånekalkylen för båda och minskar hur mycket ni kan låna till bostad. Samtidigt väger en samlad, skött skuld ofta lättare än sju spretiga krediter." },

      { t: "h2", text: "Källor" },
      {
        t: "ul",
        items: [
          "[Finansinspektionen](https://www.fi.se) – tillståndsregister och tillsyn",
          "[Konsumentverket](https://www.konsumentverket.se) – räntetak, kostnadstak och ångerrätt",
          "[Riksbanken](https://www.riksbank.se) – referensränta",
          "[Kronofogden](https://www.kronofogden.se) – vad som händer vid utebliven betalning",
        ],
      },
    ],
  },
};
